import styled from "@emotion/styled";
import React, { HTMLAttributes, useRef } from "react";
import { Column, Row } from "@src/components/Flex";
import SizedBox from "@components/SizedBox";
import Text from "@components/Text";
import Balance from "@src/entities/Balance";
import SquareTokenIcon from "@components/SquareTokenIcon";
import star from "@src/assets/icons/star.svg";
import starHover from "@src/assets/icons/star-hover.svg";
import starred from "@src/assets/icons/filled-star.svg";
import starredHover from "@src/assets/icons/filled-star-hover.svg";
import eyeIcon from "@src/assets/icons/eye.svg";
import eyeHoverIcon from "@src/assets/icons/eye-hover.svg";
import eyeOffIcon from "@src/assets/icons/eye-off.svg";
import eyeOffHoverIcon from "@src/assets/icons/eye-off-hover.svg";
import useHover from "@src/hooks/useHover";

interface IProps extends HTMLAttributes<HTMLDivElement> {
  token: Balance;
  withClickLogic?: boolean;
  hidden?: boolean;
  isFavorite?: boolean;
  onFavoriteToggle?: (assetId: string) => void;
  isHidden?: boolean;
  onHideToggle?: (assetId: string) => void;
}

const Root = styled.div<{ withClickLogic?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  cursor: ${({ withClickLogic }) => (withClickLogic ? "pointer" : "default")};
  padding: 10px 24px;

  :hover {
    background: ${({ withClickLogic, theme }) => withClickLogic && `${theme.colors.primary100}`};
  }
`;
const DefaultIcon = styled.div`
  width: 40px;
  height: 40px;
  color: ${({ theme }) => theme.colors.primary100};
  border: 1px solid ${({ theme }) => theme.colors.primary100};
  border-radius: 8px;
`;
const Name = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.primary800};
`;
const Symbol = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.primary650};

  text-transform: uppercase;
`;
const Gradient = styled.div`
  display: flex;
  bottom: 0;
  left: 0;
  top: 0;
  width: 100%;
  position: absolute;
  background: ${({ theme }) => theme.colors.gradient};
  z-index: 10;
  cursor: not-allowed;
`;
const FavIcon = styled.img`
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
`;
const HideIcon = styled.img`
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
`;

const TokenInfo: React.FC<IProps> = ({ token, hidden, isFavorite, onFavoriteToggle, isHidden, onHideToggle, ...rest }) => {
  const favRef = useRef(null!);
  const hideRef = useRef(null!);
  const isFavHover = useHover(favRef);
  const isHideHover = useHover(hideRef);

  const handleFavClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFavoriteToggle?.(token.assetId);
  };

  const handleHideClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onHideToggle?.(token.assetId);
  };

  return (
    <Root {...rest}>
      {hidden && <Gradient />}
      <Row alignItems="center">
        {onFavoriteToggle && (
          <>
            <FavIcon
              ref={favRef}
              src={isFavorite ? (isFavHover ? starredHover : starred) : isFavHover ? starHover : star}
              onClick={handleFavClick}
            />
            <SizedBox width={8} />
          </>
        )}
        {onHideToggle && (
          <>
            <HideIcon
              ref={hideRef}
              src={isHidden ? (isHideHover ? eyeOffHoverIcon : eyeOffIcon) : isHideHover ? eyeHoverIcon : eyeIcon}
              onClick={handleHideClick}
              title={isHidden ? "Show token" : "Hide token"}
            />
            <SizedBox width={8} />
          </>
        )}
        {token.logo ? <SquareTokenIcon size="small" src={token.logo} /> : <DefaultIcon />}
        <SizedBox width={8} />
        <Column>
          <Name>{token.name}</Name>
          <Symbol>{token.symbol}</Symbol>
        </Column>
      </Row>
      <Column alignItems="flex-end">
        <Text style={{ whiteSpace: "nowrap" }} textAlign="right">
          {token.formatBalance}
        </Text>
        <Text style={{ whiteSpace: "nowrap" }} textAlign="right" type="secondary" size="small">
          {token.formatUsdnEquivalent}
        </Text>
      </Column>
    </Root>
  );
};
export default TokenInfo;
